import { POWERSCHOOL_SCHEMA_KNOWLEDGE } from "@Simplicity-Education/core";
import { Button, Checkbox } from "@simplicity/ui";
import { X } from "lucide-react";
import { SinceDateInput } from "./SinceDateInput";

/**
 * Slice C filter shape persisted at `overrides.filters` on a schedule or table entry, mirroring
 * `powerschoolOverrideFiltersSchema` in `packages/core/src/schemas.ts`. SIM-1202 (SIM-1133) removed
 * the year/term dimensions after an audit found 0 of 11 live PS connectors set them at either
 * layer; they remain in core's schema but the admin UI no longer exposes them.
 */
export interface OverrideFilters {
  schoolids?: string[];
  sinceDate?: string;
  schoolYearStart?: string;
  unfiltered?: boolean;
  disable?: ("schoolids" | "sinceDate")[];
}

interface InheritedFilters {
  /** Effective schoolids at the parent layer (project → table). */
  schoolids: string[];
  /** Effective sinceDate at the parent layer. */
  sinceDate: string;
  /** Source label for the effective values. */
  source: "project" | "table";
}

interface CadenceFiltersEditorProps {
  /** Current `overrides.filters` for this cadence entry (may be undefined). */
  filters: OverrideFilters | undefined;
  /** Inherited values from the parent layer for dimmed display when no
   *  active override exists at this layer. */
  inherited: InheritedFilters;
  /** Project-level schoolYearStart for SCHOOLYEARSTART resolution. */
  schoolYearStart?: string;
  /** Frozen "now" for testability. */
  now?: Date;
  /**
   * SIM-1026: table name + fields used to consult the knowledge file and gate
   * per-dimension rows so we don't render `schoolIDs (from project)` for tables
   * the runner would never apply that filter to. Both optional for backward
   * compat — when absent, all rows render (legacy Slice C behavior).
   */
  tableName?: string;
  tableFields?: string[];
  onChange: (next: OverrideFilters | undefined) => void;
}

type DimensionKey = "schoolids" | "sinceDate";

/** Strip empty/undefined entries to keep the on-disk shape clean. */
function cleanFilters(filters: OverrideFilters): OverrideFilters {
  const next: OverrideFilters = { ...filters };
  for (const key of Object.keys(next) as (keyof OverrideFilters)[]) {
    const v = next[key];
    if (v === undefined) delete next[key];
    else if (Array.isArray(v) && v.length === 0) delete next[key];
  }
  return next;
}

/**
 * Knowledge-aware classification of how the runner will treat this table.
 * Mirrors the backend's `classifyTableClass` in effectiveScope.handlers.ts
 * (SIM-1026). Returning `null` means "no gating info — render every row"
 * (back-compat path for callers that haven't threaded tableName yet).
 */
type TableClass = "scoped-direct" | "inherited" | "reference" | "unknown";

interface TableGating {
  tableClass: TableClass;
  /**
   * `parentColumn` (Slice F.2 / SIM-1042) surfaces the non-PK column on the
   * parent that populates the IN-list when the knowledge file declares it
   * (e.g. `schools.school_number` for the `incident` cascade). Omitted ⇒
   * runner uses parent's primaryKey.
   */
  inheritedFrom?: { parent: string; joinColumn: string; parentColumn?: string };
  /** Per-dimension presence — true means render that row. */
  appliesSchoolids: boolean;
  appliesSinceDate: boolean;
}

/** Knowledge entry for `tableName` (case-insensitive), or undefined. */
type Meta = ReturnType<typeof getKnowledgeMeta>;
function getKnowledgeMeta(tableName: string) {
  return POWERSCHOOL_SCHEMA_KNOWLEDGE[tableName.toLowerCase()];
}

/**
 * Per-dimension `applies` flags for the scoped-direct / unknown path.
 */
function computeDimensionApplies(meta: Meta, tableFields: string[] | undefined) {
  const lowered = (tableFields ?? []).map((f) => f.toLowerCase());
  return {
    appliesSchoolids: meta ? meta.schoolColumns.length > 0 : lowered.includes("schoolid"),
    appliesSinceDate: meta ? meta.primaryDateColumn !== null : false,
  };
}

function gateAllOff(): Pick<TableGating, "appliesSchoolids" | "appliesSinceDate"> {
  return {
    appliesSchoolids: false,
    appliesSinceDate: false,
  };
}

function resolveTableGating(
  tableName: string | undefined,
  tableFields: string[] | undefined,
): TableGating | null {
  if (!tableName) return null;
  const meta = getKnowledgeMeta(tableName);

  if (meta?.treatAs === "reference") {
    return { tableClass: "reference", ...gateAllOff() };
  }
  if (meta?.inheritFrom) {
    // SIM-1084: hybrid tables (e.g. incident) declare inheritFrom AND scope
    // columns (primaryDateColumn, etc.). The runner composes both at sync
    // time. The editor must surface BOTH the inheritedFrom banner AND any
    // applicable dimension rows.
    const dims = computeDimensionApplies(meta, tableFields);
    return {
      tableClass: "inherited",
      inheritedFrom: { ...meta.inheritFrom },
      ...dims,
    };
  }

  const dims = computeDimensionApplies(meta, tableFields);
  return {
    tableClass: meta || dims.appliesSchoolids ? "scoped-direct" : "unknown",
    ...dims,
  };
}

function SinceDateRow(props: {
  override?: string;
  inherited: InheritedFilters;
  schoolYearStart?: string;
  now?: Date;
  unfiltered: boolean;
  disabledDim: boolean;
  onWrite: (next: string) => void;
  onToggleDisable: (on: boolean) => void;
}) {
  return (
    <tr>
      <td className="py-1 pr-2 align-top font-mono">sinceDate</td>
      <td className="py-1 pr-2 align-top">
        <SinceDateInput
          value={props.inherited.sinceDate}
          override={props.override}
          inheritedFrom={props.inherited.source}
          schoolYearStart={props.schoolYearStart}
          now={props.now}
          tokens={["SCHOOLYEARSTART", "P-1D", "P-7D", "P-30D"]}
          onChange={props.onWrite}
          disabled={props.unfiltered || props.disabledDim}
        />
      </td>
      <td className="py-1 align-top">
        <Checkbox
          checked={props.disabledDim}
          onCheckedChange={(v) => props.onToggleDisable(v === true)}
          aria-label="Disable sinceDate filter for this cadence"
          disabled={props.unfiltered}
        />
      </td>
    </tr>
  );
}

/**
 * `unfiltered` is no longer settable here (SIM-3518) — it bypasses every dimension at once,
 * which is only ever equivalent to disabling them individually while the connector happens to
 * have no `schoolids`. It still greys the rows below it, so a value left in a hand-edited
 * config says so rather than leaving the controls disabled for no visible reason.
 */
function UnfilteredNotice() {
  return (
    <tr>
      <td className="py-1 pr-2 align-top font-mono text-warning">unfiltered</td>
      <td className="py-1 pr-2 align-top text-xs text-warning" colSpan={2}>
        This entry bypasses ALL scoping. Set in config and no longer editable here — clear it with
        &ldquo;Clear cadence overrides&rdquo;, or disable dimensions individually.
      </td>
    </tr>
  );
}

function ReferenceTableNotice() {
  return (
    <div className="rounded border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground italic">
      Reference table — district-wide, no scoping applies.
    </div>
  );
}

function InheritedFromBanner(props: { parent: string; joinColumn: string; parentColumn?: string }) {
  // Slice F.2 (SIM-1042): when parentColumn is set, the cascade joins through
  // a non-PK column on the parent (e.g. schools.school_number → School_Number).
  // Render the full join shape so admins can see which parent column drives
  // the IN-list. Omitted ⇒ original Slice F PK-based cascade.
  const join = props.parentColumn ? (
    <>
      {props.parent}.{props.parentColumn} → {props.joinColumn}
    </>
  ) : (
    <>
      {props.parent}.{props.joinColumn}
    </>
  );
  return (
    <div className="rounded border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
      Scoped via parent: <span className="font-mono">{join}</span> — overrides below apply only
      after inheritance.
    </div>
  );
}

interface DimensionRowsTableProps {
  filters: OverrideFilters | undefined;
  inherited: InheritedFilters;
  schoolYearStart?: string;
  now?: Date;
  unfiltered: boolean;
  disabled: Set<DimensionKey>;
  showSinceDate: boolean;
  writeFilters: (patch: Partial<OverrideFilters>) => void;
  toggleDisable: (dim: DimensionKey, on: boolean) => void;
}

function DimensionRowsTable(props: DimensionRowsTableProps) {
  const {
    filters,
    inherited,
    schoolYearStart,
    now,
    unfiltered,
    disabled,
    showSinceDate,
    writeFilters,
    toggleDisable,
  } = props;
  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="text-muted-foreground">
          {/* Narrow enough that the heading wraps at its own space: the column was as wide
              as "Filter dimension" on one line, and the width it gives back goes to Value,
              which holds the editable content. Wrapping rather than a <br> keeps the
              accessible name "Filter dimension" instead of "Filterdimension". */}
          <th className="w-16 pb-1 text-left font-medium">Filter dimension</th>
          <th className="pb-1 text-left font-medium">Value</th>
          <th className="w-20 pb-1 text-left font-medium">Disable</th>
        </tr>
      </thead>
      <tbody>
        {showSinceDate && (
          <SinceDateRow
            override={filters?.sinceDate}
            inherited={inherited}
            schoolYearStart={schoolYearStart}
            now={now}
            unfiltered={unfiltered}
            disabledDim={disabled.has("sinceDate")}
            onWrite={(next) => writeFilters({ sinceDate: next })}
            onToggleDisable={(on) => toggleDisable("sinceDate", on)}
          />
        )}
        {unfiltered && <UnfilteredNotice />}
      </tbody>
    </table>
  );
}

interface DimensionToggles {
  showSinceDate: boolean;
  isInherited: boolean;
  showTable: boolean;
}

function computeToggles(gating: TableGating | null, unfiltered: boolean): DimensionToggles {
  // Null gating preserves pre-SIM-1026 behavior (render every row).
  const showSinceDate = gating ? gating.appliesSinceDate : true;
  const isInherited = gating?.tableClass === "inherited";
  // Only sinceDate and the unfiltered notice can occupy a row now that schoolIDs is a
  // connector setting (SIM-3517) and unfiltered is read-only (SIM-3518) — with neither,
  // the table would render as a bare header row, which says less than nothing.
  const showTable = showSinceDate || unfiltered;
  return { showSinceDate, isInherited, showTable };
}

function ClearOverridesButton({ onClick }: { onClick: () => void }) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="h-6 gap-1 text-xs text-muted-foreground"
      onClick={onClick}
    >
      <X className="h-3 w-3" />
      Clear cadence overrides
    </Button>
  );
}

/**
 * Flat-inline editor for Slice C per-cadence filter overrides. SIM-1026: knowledge-aware
 * per-dimension gating — it consults the knowledge file by `tableName` to decide which rows to
 * render. Reference tables show a notice and suppress the editor; inheritFrom tables show a banner
 * but stay editable, since the user may disable inheritance or bypass via `unfiltered`.
 */
export function CadenceFiltersEditor(props: CadenceFiltersEditorProps) {
  const { filters, inherited, schoolYearStart, now, tableName, tableFields, onChange } = props;
  const unfiltered = filters?.unfiltered === true;
  const disabled = new Set(filters?.disable ?? []);
  const gating = resolveTableGating(tableName, tableFields);

  const writeFilters = (patch: Partial<OverrideFilters>) => {
    const next = cleanFilters({ ...(filters ?? {}), ...patch });
    onChange(Object.keys(next).length === 0 ? undefined : next);
  };

  const toggleDisable = (dim: DimensionKey, on: boolean) => {
    const current = filters?.disable ?? [];
    const next: OverrideFilters["disable"] = on
      ? [...current, dim]
      : current.filter((d) => d !== dim);
    writeFilters({ disable: next });
  };

  // SIM-1026: reference tables suppress the editor entirely.
  if (gating?.tableClass === "reference") {
    return <ReferenceTableNotice />;
  }

  const { showSinceDate, isInherited, showTable } = computeToggles(gating, unfiltered);

  return (
    <div className="space-y-2">
      {isInherited && gating?.inheritedFrom && (
        <InheritedFromBanner
          parent={gating.inheritedFrom.parent}
          joinColumn={gating.inheritedFrom.joinColumn}
          parentColumn={gating.inheritedFrom.parentColumn}
        />
      )}
      {showTable && (
        <DimensionRowsTable
          filters={filters}
          inherited={inherited}
          schoolYearStart={schoolYearStart}
          now={now}
          unfiltered={unfiltered}
          disabled={disabled}
          showSinceDate={showSinceDate}
          writeFilters={writeFilters}
          toggleDisable={toggleDisable}
        />
      )}
      {filters && <ClearOverridesButton onClick={() => onChange(undefined)} />}
    </div>
  );
}
